<template>
  <div
    ref="containerRef"
    class="cropper-root"
    @mousedown="handlePointerDown"
    @mousemove="handlePointerMove"
    @mouseup="handlePointerUp"
    @mouseleave="handlePointerUp"
    @wheel="handleWheel"
  >
    <div 
      v-if="cropBox" 
      class="cropper-white-background"
      :style="{ 
        top: cropBox.top + 'px', 
        left: cropBox.left + 'px', 
        width: cropBox.width + 'px', 
        height: cropBox.height + 'px',
        backgroundColor: backgroundColor,
        borderRadius: roundBorder ? '100%' : '0'
      }"
    ></div>

    <div class="cropper-canvas-wrapper">
      <img
        v-show="initialized && reactiveImageSrc"
        :style="{
            transform: 'translate(' + (crop.x + gridOffset.x) + 'px, ' + (crop.y + gridOffset.y) + 'px) rotate(' + rotation + 'deg) scale(' + zoom + ')'
        }"
        ref="imageRef"
        :src="reactiveImageSrc"
        alt="Editing"
        draggable="false"
        class="cropper-image"
        @load="onImageLoad"
      />
    </div>

    <div v-if="cropBox && containerRect" class="cropper-overlay">
      <div
        v-if="roundBorder"
        class="cropper-overlay-round"
        :style="roundOverlayStyle"
      ></div>
      <template v-else>
        <div :style="{ position: 'absolute', top: 0, left: 0, width: '100%', height: cropBox.top + 'px', backgroundColor: 'rgba(0,0,0,0.6)' }"></div>
        <div :style="{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: (containerRect.height - cropBox.top - cropBox.height) + 'px', backgroundColor: 'rgba(0,0,0,0.6)' }"></div>
        <div :style="{ position: 'absolute', top: cropBox.top + 'px', left: 0, width: cropBox.left + 'px', height: cropBox.height + 'px', backgroundColor: 'rgba(0,0,0,0.6)' }"></div>
        <div :style="{ position: 'absolute', top: cropBox.top + 'px', right: 0, width: (containerRect.width - cropBox.left - cropBox.width) + 'px', height: cropBox.height + 'px', backgroundColor: 'rgba(0,0,0,0.6)' }"></div>
      </template>
    </div>

    <div v-if="cropBox && initialized" class="cropper-grid" 
            :style="{ top: cropBox.top + 'px', left: cropBox.left + 'px', width: cropBox.width + 'px', height: cropBox.height + 'px', borderRadius: roundBorder ? '100%' : '0' }">
        <div class="grid-line line-h-1"></div>
        <div class="grid-line line-h-2"></div>
        <div class="grid-line line-v-1"></div>
        <div class="grid-line line-v-2"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomCropper",
  props: {
    imageSrc: { type: String, required: true },
    editState: { type: Object, required: true },
    aspectRatio: { type: [String, Number], required: true },
    topPadding: { type: Number, default: 0 },
    fitMode: { type: String, default: 'cover', validator: (val) => ['cover', 'contain'].includes(val) },
    backgroundColor: { type: String, default: '#000000' },
    roundBorder: { type: Boolean, default: false },
  },
  data() {
    return {
      isDragging: false,
      lastPos: { x: 0, y: 0 },
      containerRect: null,
      cropBox: null,
      resizeObserver: null,
      gridOffset: { x: 0, y: 0 },
      reactiveImageSrc: this.imageSrc,
      reactiveEditState: this.editState
    };
  },
  computed: {
    crop() {
      return this.reactiveEditState.crop;
    },
    zoom() {
      return this.reactiveEditState.zoom;
    },
    rotation() {
      return this.reactiveEditState.rotation;
    },
    initialized() {
      return this.reactiveEditState.initialized;
    },
    constraintDeps() {
      return {
        rotation: this.rotation,
        zoom: this.zoom,
      };
    },
    roundOverlayStyle() {
      if (!this.cropBox) return {};
      const centerX = this.cropBox.left + this.cropBox.width / 2;
      const centerY = this.cropBox.top + this.cropBox.height / 2;
      const radiusX = this.cropBox.width / 2;
      const radiusY = this.cropBox.height / 2;
      return {
        background: `radial-gradient(ellipse ${radiusX}px ${radiusY}px at ${centerX}px ${centerY}px, transparent 99%, rgba(0,0,0,0.6) 100%)`
      };
    },
  },
  watch: {
    imageSrc: {
      handler(newVal) {
        this.reactiveImageSrc = newVal;
      },
      immediate: true
    },
    editState: {
      handler(newVal) {
        Object.assign(this.reactiveEditState, newVal);
      },
      deep: true,
      immediate: true
    },
    aspectRatio() {
      this.updateContainer();
      this.$nextTick(this.initZoomAndCenter);
    },
    topPadding() {
      this.updateContainer();
    },
    fitMode() {
      // Reinitialize when fit mode changes
      if (this.initialized) {
        this.$nextTick(this.initZoomAndCenter);
      }
    },
    constraintDeps: {
      deep: true,
      handler() {
        this.handleConstraints();
      },
    },
    cropBox: {
      deep: true,
      handler() {
        if (
          !this.initialized &&
          this.$refs.imageRef &&
          this.$refs.imageRef.complete
        ) {
          this.initZoomAndCenter();
          this.handleConstraints();
        }
      },
    },
  },
  mounted() {
    this.updateContainer();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.updateContainer);
      if (this.$refs.containerRef)
        this.resizeObserver.observe(this.$refs.containerRef);
    }
    window.addEventListener("resize", this.updateContainer);
    // Check if image is already loaded from cache
    if (this.$refs.imageRef && this.$refs.imageRef.complete) {
      this.onImageLoad();
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.updateContainer);
  },
  methods: {
    emitUpdate(payload) {
      if (this.cropBox) {
        payload.refWidth = this.cropBox.width;
        payload.refHeight = this.cropBox.height;
      }
      this.$emit("update", payload);
    },
    initZoomAndCenter(isFirstInit = false) {
      if (!this.$refs.imageRef || !this.cropBox) return;

      const img = this.$refs.imageRef;
      const vpWidth = this.cropBox.width;
      const vpHeight = this.cropBox.height;
      const vpRatio = vpWidth / vpHeight;

      const rotated = this.rotation % 180 !== 0;
      const imgW = rotated ? img.naturalHeight : img.naturalWidth;
      const imgH = rotated ? img.naturalWidth : img.naturalHeight;
      const imgRatio = imgW / imgH;

      let initialZoom;
      if (this.fitMode === 'contain') {
        // Contain: fit image within crop box (use smaller scale)
        initialZoom = vpRatio > imgRatio ? vpHeight / imgH : vpWidth / imgW;
      } else {
        // Cover: fill crop box (use larger scale)
        initialZoom = vpRatio > imgRatio ? vpWidth / imgW : vpHeight / imgH;
      }

      this.emitUpdate({
        zoom: initialZoom,
        crop: { x: 0, y: 0 },
        initialized: true,
        isFirstInit
      });
    },
    updateContainer() {
      const el = this.$refs.containerRef;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      this.containerRect = {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      };

      const targetRatio = this.aspectRatio || 1;
      let padding = 100;
      if (rect.height < 200) padding = 0;
      
      const availW = rect.width - padding;
      const availH = rect.height - padding;

      let w = availW;
      let h = w / targetRatio;

      if (h > availH) {
        h = availH;
        w = h * targetRatio;
      }

      const left = (rect.width - w) / 2;
      const top = this.topPadding;

      this.cropBox = { width: w, height: h, left, top };

      const gridCX = left + w / 2;
      const gridCY = top + h / 2;

      const contCX = rect.width / 2;
      const contCY = rect.height / 2;

      this.gridOffset = {
        x: gridCX - contCX,
        y: gridCY - contCY
      };
    },
    getBounds() {
      if (!this.$refs.imageRef || !this.cropBox) return { maxX: 0, maxY: 0 };

      const img = this.$refs.imageRef;
      const vpWidth = this.cropBox.width;
      const vpHeight = this.cropBox.height;

      const rotated = this.rotation % 180 !== 0;
      const w = rotated ? img.naturalHeight : img.naturalWidth;
      const h = rotated ? img.naturalWidth : img.naturalHeight;

      const scaledW = w * this.zoom;
      const scaledH = h * this.zoom;

      return {
        maxX: Math.max(0, (scaledW - vpWidth) / 2),
        maxY: Math.max(0, (scaledH - vpHeight) / 2),
      };
    },
    handleConstraints(isFirstInit = false) {
      if (this.$refs.imageRef && this.cropBox) {
        const img = this.$refs.imageRef;
        const vpWidth = this.cropBox.width;
        const vpHeight = this.cropBox.height;
        const vpRatio = vpWidth / vpHeight;

        const rotated = this.rotation % 180 !== 0;
        const imgW = rotated ? img.naturalHeight : img.naturalWidth;
        const imgH = rotated ? img.naturalWidth : img.naturalHeight;
        const imgRatio = imgW / imgH;

        let newMinZoom;
        if (this.fitMode === 'contain') {
          // Contain: minimum zoom to fit within crop box
          newMinZoom = vpRatio > imgRatio ? vpHeight / imgH : vpWidth / imgW;
        } else {
          // Cover: minimum zoom to fill crop box
          newMinZoom = vpRatio > imgRatio ? vpWidth / imgW : vpHeight / imgH;
        }
        this.$emit("minZoomChange", newMinZoom);

        if (this.zoom < newMinZoom) {
          if (Math.abs(this.zoom - newMinZoom) > 0.001) {
            this.emitUpdate({ zoom: newMinZoom, crop: { x: 0, y: 0 }, isFirstInit });
          }
        } else {
          const { maxX, maxY } = this.getBounds();
          const newX = Math.max(-maxX, Math.min(maxX, this.crop.x));
          const newY = Math.max(-maxY, Math.min(maxY, this.crop.y));

          if (newX !== this.crop.x || newY !== this.crop.y) {
            this.emitUpdate({ crop: { x: newX, y: newY }, isFirstInit });
          }
        }
      }
    },
    handlePointerDown(e) {
      e.preventDefault();
      this.isDragging = true;
      this.lastPos.x = e.clientX;
      this.lastPos.y = e.clientY;
    },
    handlePointerMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const dx = e.clientX - this.lastPos.x;
      const dy = e.clientY - this.lastPos.y;
      this.lastPos.x = e.clientX;
      this.lastPos.y = e.clientY;

      const { maxX, maxY } = this.getBounds();
      const newX = Math.max(-maxX, Math.min(maxX, this.crop.x + dx));
      const newY = Math.max(-maxY, Math.min(maxY, this.crop.y + dy));

      this.emitUpdate({ crop: { x: newX, y: newY } });
    },
    handlePointerUp() {
      this.isDragging = false;
    },
    handleWheel(e) {
      e.preventDefault();
      let newZoom = this.zoom - e.deltaY * 0.001;
      newZoom = Math.min(3, newZoom);
      this.emitUpdate({ zoom: newZoom });
    },
    onImageLoad() {
      if (!this.initialized) this.initZoomAndCenter(true);
      this.handleConstraints(true);
    },
  },
};
</script>

<style scoped>
/* Cropper Component */
.cropper-root {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #333;
  cursor: move;
  user-select: none;
  touch-action: none;
}

.cropper-canvas-wrapper {
  position: absolute;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cropper-image {
  max-width: none;
  transform-origin: center;
}

.cropper-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.cropper-overlay-round {
  position: absolute;
  inset: 0;
}

.cropper-white-background {
  position: absolute;
  pointer-events: none;
}

.cropper-grid {
  position: absolute;
  pointer-events: none;
  border: 1.5px solid #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
}

.grid-line {
  position: absolute;
  background-color: transparent;
}
.line-h-1 {
  top: 33.33%;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px dashed #FFFFFF;
}
.line-h-2 {
  bottom: 33.33%;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px dashed #FFFFFF;
}
.line-v-1 {
  left: 33.33%;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed #FFFFFF;
}
.line-v-2 {
  right: 33.33%;
  top: 0;
  bottom: 0;
  width: 0;
  border-right: 1px dashed #FFFFFF;
}
</style>